import { Text } from "@clipboard-health/ui-components";
import { keyframes } from "@emotion/react";
import { Box, Stack } from "@mui/material";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { useRef, useState } from "react";

interface VoiceNoteRecorderProps {
  onChange: (value: Blob | undefined) => void;
}

export function VoiceNoteRecorder(props: VoiceNoteRecorderProps) {
  const { onChange } = props;

  const mediaRecorderRef = useRef<MediaRecorder>();
  const [isRecording, setIsRecording] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | undefined>();

  const [error, setError] = useState<string | undefined>();

  const blink = keyframes`
 0% { opacity: 1; }
 50% { opacity: 0.2; }
 100% { opacity: 1; }
 `;

  async function startRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = "audio/mp4";

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      const chunks: BlobPart[] = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: mimeType });

        // FIXME: Automatically disabled during ESLint config update
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
        // Pass the recorded blob to the parent form
        onChange(blob);
      };

      mediaRecorder.start();
      setIsRecording(true);
      setError(undefined);
      // clear any previous value
      setAudioUrl(undefined);
      onChange(undefined);
    } catch {
      setError("Could not access microphone");
    }
  }

  function stopRecording() {
    if (!mediaRecorderRef.current) {
      return;
    }

    mediaRecorderRef.current.stop();
    mediaRecorderRef.current.stream.getTracks().forEach((track) => {
      track.stop();
    });
    setIsRecording(false);
  }

  function deleteAudio() {
    if (audioUrl) {
      // FIXME: Automatically disabled during ESLint config update
      URL.revokeObjectURL(audioUrl);
    }

    setAudioUrl(undefined);
    onChange(undefined);
  }

  return (
    <Stack spacing={2}>
      <Text variant="body1">Voice Note (optional)</Text>
      <Text variant="caption">
        You can record a voice note to help the workplace understand why you are a good fit for the
        job.
      </Text>

      {error && (
        <Text variant="body2" color={(theme) => theme.palette.error.main}>
          {error}
        </Text>
      )}

      {audioUrl && (
        <Stack direction="row" spacing={1} alignItems="center">
          <audio controls src={audioUrl} style={{ flex: 1 }}>
            <track kind="captions" />
          </audio>
          <IconButton
            variant="outlined"
            iconType="remove"
            aria-label="delete audio"
            onClick={() => {
              deleteAudio();
            }}
          />
        </Stack>
      )}

      {/* Recording indicator */}
      {isRecording && (
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            component="span"
            sx={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              backgroundColor: (theme) => theme.palette.error.main,
              animation: `${blink} 1s infinite`,
            }}
          />
          <Text variant="body2">Recording...</Text>
        </Stack>
      )}

      <Stack direction="row" spacing={2}>
        {!isRecording && !audioUrl && (
          <LoadingButton
            variant="outlined"
            size="small"
            isLoading={false}
            onClick={async () => {
              await startRecording();
            }}
          >
            Start Recording
          </LoadingButton>
        )}

        {isRecording && (
          <LoadingButton
            variant="contained"
            size="small"
            isLoading={false}
            onClick={() => {
              stopRecording();
            }}
          >
            Stop Recording
          </LoadingButton>
        )}
      </Stack>
    </Stack>
  );
}
